import { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { InvalidateItemPageDataQuery } from '../graphql/graphql';
import { invalidateItemPageData } from '../api/queries';
import Bugsnag from '../utils/bugsnag';
import { ItemStatusEnum } from '../graphql/types';

const isE2ETest = process.env.NEXT_PUBLIC_E2E === '1';

export const useItemRevalidation = (
  itemId: string,
  remoteUpdatedAt: string,
  isrVersion: string,
  nonSsrCurrentItemDataState: ItemStatusEnum | undefined,
  nonSsrCurrentItemUpdatedAt: string,
  nonSsrCurrentItemRefetch: unknown
) => {
  const { i18n } = useTranslation();
  const hasNoCache =
    !remoteUpdatedAt && nonSsrCurrentItemDataState === ItemStatusEnum.Published;
  const actualUpdatedAt = hasNoCache
    ? nonSsrCurrentItemUpdatedAt
    : remoteUpdatedAt;

  const [invalidateItemPageQuery, { data, loading }] =
    useLazyQuery<InvalidateItemPageDataQuery>(invalidateItemPageData, {
      variables: { id: itemId },
      fetchPolicy: 'network-only'
    });

  const refetch = () => {
    if (hasNoCache) nonSsrCurrentItemRefetch();
    else invalidateItemPageQuery();
  };

  useEffect(() => {
    if (!isE2ETest) {
      const storageKey = `revalidate_lock_${itemId}_${actualUpdatedAt}_${isrVersion}`;

      if (sessionStorage.getItem(storageKey)) {
        refetch();
        return;
      }

      const remoteTime = new Date(actualUpdatedAt).getTime();
      const isrTime = new Date(isrVersion).getTime();
      if (remoteTime && remoteTime > isrTime) {
        refetch();

        fetch('/api/revalidate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            itemId,
            isrTime,
            lang: i18n.language
          })
        })
          .then(async response => {
            const res = await response.json();

            if (res?.success && res?.revalidated)
              sessionStorage.setItem(storageKey, 'true');
          })
          .catch(err => {
            console.error('Revalidation error:', err);
            Bugsnag.notify(err, event => {
              event.addMetadata('revalidation', {
                itemId,
                isrVersion,
                actualUpdatedAt
              });
            });
          });
      }
    }
  }, [
    actualUpdatedAt,
    isrVersion,
    itemId,
    i18n.language,
    invalidateItemPageQuery
  ]);

  useEffect(() => {
    const remoteTime = new Date(actualUpdatedAt).getTime();
    const isrTime = new Date(isrVersion).getTime();
    if (remoteTime === isrTime) {
      const prefix = `revalidate_lock_${itemId}`;

      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith(prefix)) sessionStorage.removeItem(key);
      });
    }
  }, [itemId, actualUpdatedAt, isrVersion]);

  return {
    isDataRefreshing: loading,
    invalidateItemData: data
  };
};
